import React, { Component } from 'react';
import PropTypes from 'prop-types';
import webRequest from '../WebRequest';
import Snackbar from '../Snackbar';
import { getRandomId } from '../Utils/baseComponentHelper';

/**
Il componente `FileUpload` permette l'upload di files,
wrappando un qualunque componente al suo interno che fungerà da trigger per l'upload.
 */
class FileUpload extends Component {
  constructor(props) {
    super(props);

    this.uniqueid = getRandomId();
  }

  updateFiles = (e) => {
    const {
      allowUpload, max, multiple, onChange, onProgress, url, onFileUploaded, onCatch,
    } = this.props;

    const { files } = e.target;
    for (let i = 0; i < files.length; i += 1) {
      const file = files[i];

      if (max && Math.ceil(file.size / 1000000) > max) {
        Snackbar.show({ type: Snackbar.Types.ERROR, message: `Il file supera la dimensione massima accettata: ${max} MB` });
        return;
      }

      let type = `.${file.name.split('.').pop()}`;
      if (type.indexOf('/') > -1) {
        type = type.substr(type.indexOf('/') + 1);
      }

      if (this.extensionAccepted(type)) {
        const formData = new FormData();
        formData.append('file', file);

        if ((allowUpload == null) || (allowUpload != null && allowUpload(file))) {
          if (!multiple) {
            onChange && onChange(e, file);
          }

          if (url) {
            webRequest.postFile(
              url,
              formData,
              null,
              (evt) => {
                const progressPercent = (evt.loaded / evt.total);
                onProgress && onProgress(evt, progressPercent, file);
              },
            )
              .then((res) => {
                this.refInput.value = '';
                onFileUploaded && onFileUploaded(res.data, file);
              })
              .catch((err) => {
                this.refInput.value = '';
                onCatch && onCatch(err, file);
              });
          }
        }
      }
    }

    if (multiple) {
      onChange && onChange(e);
    }
  };

  extensionAccepted = (extension) => {
    const { accept } = this.props;
    if ((accept && accept.map((x) => x.toLowerCase())
      .indexOf(extension.toLowerCase()) === -1)) {
      Snackbar.show({ type: Snackbar.Types.ERROR, message: `Tipo di file non supportato: ${extension}` });
      return false;
    }

    return true;
  };

  render() {
    const {
      children,
      accept,
      multiple,
      disabled,
      label,
    } = this.props;

    return (
      <div className="upload">
        <div
          className="upload__button"
          role="presentation"
          onClick={() => !disabled && this.refInput.click()}
        >
          {children}
        </div>
        {label && <label htmlFor={this.uniqueid} className="input__label">{label}</label>}
        <input
          id={this.uniqueid}
          autoComplete={`fileupload-${this.uniqueid}`}
          disabled={disabled}
          multiple={multiple}
          accept={(accept && accept.map((x) => x).join(',')) || 'true'}
          className="upload__input"
          data-origin="fileupload"
          onChange={this.updateFiles}
          ref={(r) => { this.refInput = r; }}
          type="file"
        />
      </div>
    );
  }
}

FileUpload.defaultProps = {
  accept: '',
  allowUpload: null,
  children: null,
  max: 10240,
  multiple: false,
  onChange: null,
  onFileUploaded: null,
  onCatch: null,
  onProgress: null,
  disabled: false,
  url: null,
  label: null,
};

FileUpload.propTypes = {
  /** Array contenente le estensioni accetatte come input */
  accept: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.string]),
  /**
   * Posso specificare se permettere o no il caricamento di un file:
   * ```
   *  allowUpload(file)
   * ```
   * dove:
   * * *file*: file da caricare
   */
  allowUpload: PropTypes.func,
  /** Contenuto da renderizzare all'interno del componente */
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  /** Indica se il componente è disabilitato */
  disabled: PropTypes.bool,
  /** Dimensione massima caricabile (in MB) */
  max: PropTypes.number,
  /** Permette di specificare se il componente consentirà l'upload di files multipli */
  multiple: PropTypes.bool,
  /**
   * Callback invocata alla modifica del file:
   * ```
   *  onChange(event, file)
   * ```
   * dove:
   * * *event*: evento scatenato al caricamento del file
   * * *file*: file caricato (solo nel caso di file singolo)
   */
  onChange: PropTypes.func,
  /**
   * Callback di avvenuto upload:
   * ```
   *  onFileUploaded(data, file)
   * ```
   * dove:
   * * *data*: risposta del server
   * * *file*: file caricato
   */
  onFileUploaded: PropTypes.func,
  /**
   * Callback di avvenuto errore (catch) durante la chiamata post al server:
   * ```
   *  onCatch(error, file)
   * ```
   * dove:
   * * *error*: errore restituito dalla catch
   * * *file*: file caricato
   */
  onCatch: PropTypes.func,
  /**
   * Callback di progresso dell'upload del file:
   * ```
   *  onProgress(event, progressPercent, file)
   * ```
   * dove:
   * * *event*: evento di progresso
   * * *progressPercent*: progressivo di caricamento in percentuale
   * * *file*: file caricato
   */
  onProgress: PropTypes.func,
  /** Indirizzo dell'endpoint al quale verrà inviato il file.
   * Se non viene passato questo parametro allora non viene effettuato
   * la post del file, ma solo gestita la selezione.
   * In quest'ultimo caso ovviamente non verranno utilizzati i
   * parametri *onFileUploaded* e *onCatch*.
   */
  url: PropTypes.string,
  /**
   * Etichetta dell'input (non visibile)
   */
  label: PropTypes.string,
};

export default FileUpload;
